import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_ITEMS } from "../constants";

const Navbar: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === "/";

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Handle navigation
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    setMobileMenuOpen(false);

    if (href.startsWith('#')) {
      e.preventDefault();
      const sectionId = href.replace('#', '');

      if (isHomePage) {
        // Just update the hash via react-router
        // The scroll handler in Home.tsx will pick this up
        navigate(href);
      } else {
        // Navigate to home with hash
        navigate('/' + href);
      }
    } else {
      // For route links like /blog, use react-router navigate for SPA experience
      e.preventDefault();
      navigate(href);
    }
  };

  return (
    <>
      {/* The Island (Closed State) */}
      <div className="fixed top-5 left-0 right-0 z-[9999] flex justify-center pointer-events-none px-4">
        <nav className="
          pointer-events-auto
          flex items-center justify-between
          glass-panel
          rounded-full
          px-8 py-4
          shadow-[0_15px_35px_rgba(0,0,0,0.5)]
          transition-all duration-500 hover:shadow-[0_20px_45px_rgba(6,182,212,0.15)]
          w-full max-w-sm lg:max-w-5xl
        ">
          {/* Logo */}
          <Link to="/" className="group relative z-[10001] flex items-center gap-3">
            <img src="/logo.png" alt="Josh Jacobson Logo" className="w-8 h-8 object-contain" />
            <span className="font-montecra text-2xl text-white tracking-tight transition-all group-hover:text-neon-blue-glow">
              J.jAcoBsoN
            </span>
            <span className="absolute -bottom-1 left-0 w-0 h-[1px] bg-neon-blue group-hover:w-full transition-all duration-500" />
          </Link>

          {/* Desktop Links */}
          <div className="hidden lg:flex items-center gap-10">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.label}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className="text-xs font-bold text-zinc-400 hover:text-neon-blue-glow transition-all uppercase tracking-[0.2em] relative group"
              >
                {item.label}
                <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-0 h-1 rounded-full bg-neon-blue-glow opacity-0 group-hover:w-1 group-hover:opacity-100 transition-all duration-300" />
              </a>
            ))}
          </div>

          {/* Mobile Hamburger */}
          <button
            className="lg:hidden text-white p-1 z-[10001] relative focus:outline-none hover:text-neon-blue-glow transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </nav>
      </div>

      {/* The Vault (Open Mobile State) */}
      <div
        className={`
          fixed inset-0 z-[9998] bg-black backdrop-blur-3xl
          transition-all duration-700 cubic-bezier(0.16, 1, 0.3, 1)
          flex flex-col justify-start pl-10 pt-32 pb-40 overflow-y-auto
          ${mobileMenuOpen ? "opacity-100 translate-x-0" : "opacity-0 translate-x-full"}
        `}
      >
        <div className="flex flex-col gap-6 md:gap-8">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.label}
              href={item.href}
              onClick={(e) => handleNavClick(e, item.href)}
              className="text-[42px] md:text-[64px] font-heading font-bold text-white hover:text-neon-blue-glow transition-all tracking-tight group flex items-center gap-4"
            >
              <span className="text-neon-blue/30 text-xl font-mono group-hover:text-neon-blue opacity-0 group-hover:opacity-100 transition-all -translate-x-4 group-hover:translate-x-0">
                /
              </span>
              {item.label}
            </a>
          ))}
        </div>

        {/* Secondary Info */}
        <div className="mt-auto pt-10 flex flex-col gap-4 pb-10 md:pb-0">
          <span className="text-xs text-zinc-500 uppercase tracking-[0.3em] font-bold">Get in touch</span>
          <a href="mailto:jacobsonjosh1@gmail.com" className="text-xl text-zinc-200 hover:text-neon-blue-glow transition-colors font-light">
            jacobsonjosh1@gmail.com
          </a>
        </div>
      </div>
    </>
  );
};

export default Navbar;
